import { Space } from 'antd/lib';
import Button      from '@widesk-ui/components/Button';
import Input       from '@widesk-ui/components/form/Input';
import React       from 'react';
import View        from '@widesk-ui/components/View';
import MixedBorder from '@/TokamapEditor/ux/AreaSidebar/MixedBorder';
import debounce    from 'lodash/debounce';

export default function InputOk(props: {
	value?: string;
	placeholder?: string;
	mixed?: boolean;
	/** Message affiché si `onSubmit` échoue sans lever d'`Error` (ex. une simple valeur rejetée).
	 * Sans effet si l'erreur levée est une instance d'`Error` : son `message` est alors utilisé
	 * (voir `AreaUrlSection`, qui lève une erreur de validation dédiée). Défaut : le message
	 * historique de cet écran, conservé pour ne pas changer le comportement des appelants existants. */
	errorMessage?: string;
	/** Libellé du bouton de validation. Défaut : `Valider`, qui convient aux quatre champs servis par
	 * ce composant (nom de thème, contenu de texte, URL d'image, URL d'action) — à ne préciser que si
	 * un contexte futur mérite un verbe plus spécifique. */
	okLabel?: React.ReactNode;
	/** Sans bouton : la valeur est soumise après ce délai sans frappe (ms), ou sur Entrée. */
	debounce?: number;
	onSubmit: (value?: string) => Promise<void>;
}) {
	const [currentValue, setCurrentValue] = React.useState(props.value);
	const [error, setError] = React.useState<string>();

	React.useEffect(() => {
		setCurrentValue(props.value);
		setError(undefined);
	}, [props.value]);

	const submit = React.useCallback(async (value?: string) => {
		try {
			setError(undefined);
			await props.onSubmit(value);
		} catch (e) {
			setError(e instanceof Error ? e.message : (props.errorMessage ?? `Impossible de charger l'image`));
		}
	}, [props.onSubmit, props.errorMessage]);

	const submitRef = React.useRef(submit);
	submitRef.current = submit;

	const debouncedSubmit = React.useMemo(
		() => debounce((value?: string) => submitRef.current(value), props.debounce),
		[props.debounce],
	);

	React.useEffect(() => () => debouncedSubmit.cancel(), [debouncedSubmit]);

	const hideOkButton = props.debounce !== undefined;

	return (
		<View>
			<MixedBorder mixed={props.mixed} br={6}>
				<Space.Compact style={{ width: '100%' }}>
					<Input
						value={currentValue}
						placeholder={props.mixed && !currentValue ? 'Valeurs différentes' : props.placeholder}
						onChange={(e: any) => {
							setCurrentValue(e.target.value);

							if (hideOkButton) debouncedSubmit(e.target.value);
						}}
						onPressEnter={async () => {
							debouncedSubmit.cancel();
							await submit(currentValue);
						}}
					/>

					{!hideOkButton && (
						<Button type="primary" onClick={() => submit(currentValue)}>
							{props.okLabel ?? 'Valider'}
						</Button>
					)}
				</Space.Compact>
			</MixedBorder>

			{!!error && (
				<View color="red">
					{error}
				</View>
			)}
		</View>
	);
}